import * as React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import cx from 'classnames';

import { HeroLayoutCover } from './components/HeroLayoutCover/HeroLayoutCover';
import { Divider } from '../../components/Divider/Divider';
import { Heading } from '../../components/Heading/Heading';
import { ThemeSentinel } from '../../components/ThemeSentinel/ThemeSentinel';
import { useHeaderHeight } from '../../utils/useHeaderHeight';

import * as cs from './HeroLayout.module.css';

interface Props {
  bgImages?: {
    default?: Queries.ServicePageEntryFragment['bgImage'];
    original?: {
      desktop: Queries.ServicePageEntryFragment['bgImageOriginal'];
      mobile: Queries.ServicePageEntryFragment['bgImageMobileOriginal'];
    };
  };
  isBgFullSize?: boolean | null;
  bgColorOverflow?: boolean | null;
  coverImage?: Queries.BlogPostPageEntryFragment['coverImage'];
  coverVideo?: Queries.BlogPostPageEntryFragment['coverVideo'];
  coverVideoLoop?: boolean | null;
  date?: string | null;
  dateISO?: string;
  title: string;
  logo?: string | null;
  infoStart?: React.ReactNode;
  infoEnd?: React.ReactNode;
  footer?: React.ReactNode;
  whiteBg: boolean;
  infoClassName?: string;
  noEndDivider?: boolean;
  subheader?: React.ReactNode;
}

export const HeroLayout = ({
  bgImages,
  isBgFullSize,
  bgColorOverflow,
  coverImage,
  coverVideo,
  coverVideoLoop,
  logo,
  subheader,
  date,
  dateISO,
  title,
  infoStart,
  infoEnd,
  footer,
  whiteBg,
  infoClassName,
  noEndDivider = false,
}: Props) => {
  const headerHeight = useHeaderHeight(Boolean(bgImages?.default));
  const rootRef = React.useRef<HTMLDivElement>(null);

  const withBgColorOverflow = bgImages?.default && bgColorOverflow;

  const desktopData =
    bgImages?.original?.desktop?.childImageSharp?.gatsbyImageData;
  const mobileData =
    bgImages?.original?.mobile?.childImageSharp?.gatsbyImageData;

  const desktopSrc = desktopData?.images?.fallback?.src;
  const mobileSrc = mobileData?.images?.fallback?.src;

  const bgImageData = bgImages?.default?.childImageSharp?.gatsbyImageData
    ? getImage(bgImages.default.childImageSharp.gatsbyImageData)
    : desktopData
      ? getImage(desktopData)
      : undefined;

  const sources =
    desktopSrc && mobileSrc
      ? [
          {
            media: '(min-width: 1280px)',
            srcSet: desktopSrc,
          },
          {
            media: '(max-width: 1280px)',
            srcSet: mobileSrc,
          },
        ]
      : undefined;

  return (
    <div
      ref={rootRef}
      className={cx(cs.root, {
        [cs.whiteBg]: whiteBg && !bgImages?.default,
        [cs.fullSizeBg]: isBgFullSize,
      })}
      style={
        {
          '--header-height': `${headerHeight}px`,
          ...(isBgFullSize && mobileData && desktopData
            ? {
                '--desktop-image-width': desktopData.width,
                '--desktop-image-height': desktopData.height,
                '--mobile-image-width': mobileData.width,
                '--mobile-image-height': mobileData.height,
                '--header-height': `${headerHeight}px`,
              }
            : undefined),
        } as React.CSSProperties
      }
    >
      {bgImageData && (
        <>
          <GatsbyImage
            className={cs.bgImage}
            image={{
              ...bgImageData,
              images: {
                ...bgImageData?.images,
                sources,
              },
            }}
            loading="eager"
            alt={`Background for ${title}`}
            objectFit={isBgFullSize ? 'contain' : 'cover'}
            aria-hidden
          />

          {withBgColorOverflow && <div className={cs.bgColorOverflow} />}
        </>
      )}

      <ThemeSentinel />

      <Heading className={cs.title} level={1}>
        {title}
      </Heading>

      {Boolean(subheader) && <div className={cs.subheader}>{subheader}</div>}
      {Boolean(date) && (
        <time className={cs.date} dateTime={dateISO && dateISO.split('T')[0]}>
          {date}
        </time>
      )}

      <HeroLayoutCover
        coverImage={coverImage}
        coverVideo={coverVideo}
        coverVideoLoop={coverVideoLoop}
        logo={logo}
        title={title}
        largePlaceholder={withBgColorOverflow}
      />

      {(infoStart || infoEnd) && (
        <div
          className={cx(cs.info, infoClassName, {
            [cs.footerBG]: isBgFullSize,
          })}
        >
          {infoStart}
          {infoStart && infoEnd && !isBgFullSize && (
            <Divider orientation={['horizontal', 'md:vertical']} isDecorative />
          )}
          {infoEnd}
        </div>
      )}

      {footer}

      {whiteBg && !noEndDivider && !isBgFullSize && <Divider />}
    </div>
  );
};
